import React, { Component } from 'react';
import { connect } from 'react-redux';
import { CButton, CPage, Text } from '@appcues/component-library';
import {
  Dropdown,
  Icon,
  Link,
  MenuList,
  SquareButton,
} from 'next/components/ui';
import styled from 'styled-components';
import IndexPage from 'components/Common/IndexPage';
import Loader from 'components/Common/Loader';
import EmptyState from 'components/Common/EmptyState';
import StepViewControls from 'components/StepIndex/StepViewControls';
import StepView from 'components/StepIndex/StepView';
import {
  selectFlowLayout,
  selectFlowStatusFilter,
  selectFlowSegmentFilter,
  selectFlowCreatedByFilter,
  selectFlowSearchFilter,
  selectFlowTagsFilter,
  selectFlowSortOption,
} from 'selectors/view';
import {
  getStepsBySearchString,
  getSortedSteps,
  getFilteredSteps,
  getTagsDropdownItems,
} from 'helpers/view';
import { CREATE_FLOW_MODAL, TAG_MANAGEMENT_MODAL } from 'constants/globals';

import {
  selectFlowsAsList,
  selectAreFlowsSynced,
} from 'reducers/account/flows';
import { selectUserId, selectUserUsageProperties } from 'reducers/user';
import { selectAccountConditions } from 'reducers/account/conditions';
import {
  selectAccountTags,
  selectAccountTagsSynced,
} from 'reducers/account/tags';
import {
  selectAccountUsers,
  selectAccountUsersSynced,
} from 'reducers/account/users';
import { selectAccountFeature } from 'reducers/account/features';
import { FLOW_PRIORITIZATION } from 'constants/features';

import {
  selectAccountMeta,
  selectAccountId,
  selectAccountMetaSynced,
} from 'reducers/account/meta';
import {
  selectAccountGoals,
  selectAccountGoalsSynced,
} from 'reducers/account/goals';
import {
  selectAccountSegments,
  selectAccountSegmentsSynced,
} from 'reducers/account/segments';
import { selectAccountStats } from 'reducers/account/stats';
import { selectFlowListAnalytics } from 'reducers/analytics';
import {
  selectHasChromeExtensionInstalled,
  selectChromeExtensionId,
} from 'reducers/chromeExtension';

import { callApi as fetchRules } from 'actions/account/rules';
import { navigate } from 'actions/routing';
import { showModal } from 'actions/currentModal';
import { fetchFlowListAnalytics } from 'actions/analytics';
import { startBuilderInChromeExtension } from 'actions/chromeExtension';
import FlowListImg from 'assets/images/flow-list.svg';

import { doubleCheckHasChromeExtension } from 'utils/chromeExtension';
import ProductLedExampleFlows from 'components/StepIndex/ProductLedExampleFlows';

const EllipsisButton = styled(SquareButton)`
  border: none;

  ${Icon} {
    color: var(--icon);
  }

  ${({ active }) =>
    active &&
    `
      background-color: var(--square-button-bg);

      ${Icon} {
        color: var(--primary);
      }
    `}
`;

export class StepIndex extends Component {
  componentDidMount() {
    const { onLoad } = this.props;
    onLoad();
  }

  componentDidUpdate() {
    this.requestAnalytics();
  }

  createFlow = () => {
    const {
      meta,
      tags,
      userId,
      chromeExtensionId,
      hasChromeExtensionInstalled,
      showModal: show,
      startBuilderInChromeExtension: startBuilder,
    } = this.props;

    const INSTALL_CRX_APPCUES_FLOW_ID = '-M3rSy4QkaoZ2YgGFHhc';

    doubleCheckHasChromeExtension(
      chromeExtensionId,
      hasChromeExtensionInstalled
    ).then(hasChromeExtension => {
      if (window.Appcues && !hasChromeExtension) {
        window.Appcues.show(INSTALL_CRX_APPCUES_FLOW_ID);
      } else {
        const domainsString = meta.domains || '';
        const domains = domainsString.split(',');
        show(CREATE_FLOW_MODAL, {
          domains,
          tags,
          startBuilderInChromeExtension: startBuilder,
          accountApiKey: meta.uuid,
          accountId: meta.id,
          userId,
        });
      }
    });
  };

  showManagementModal = () => {
    const { showModal: show } = this.props;
    show(TAG_MANAGEMENT_MODAL, {});
  };

  requestAnalytics(retry = false) {
    const {
      accountLoaded,
      accountId,
      flowListAnalytics,
      fetchFlowListAnalytics: fetchAnalytics,
    } = this.props;

    if (
      accountLoaded &&
      !flowListAnalytics.meta.fetching &&
      (!(flowListAnalytics.meta.synced || flowListAnalytics.meta.errored) ||
        retry)
    ) {
      fetchAnalytics(accountId);
    }
  }

  render() {
    const {
      loaded,
      flows,
      conditions,
      users,
      tags,
      meta,
      goals,
      segments,
      flowListAnalytics,
      flowLayout,
      statusFilter,
      segmentFilter,
      createdByFilter,
      searchFilter,
      tagsFilter,
      sortOption,
      className,
      usageProperties,
      hasFlowPrioritization,
    } = this.props;

    if (!loaded) {
      return <Loader />;
    }

    const hasNoFlows = flows.length === 0;

    const stepsWithFilters = getFilteredSteps(flows, conditions, {
      statusFilter,
      createdByFilter,
      tagsFilter,
      segmentFilter,
    });

    const stepsFromSearch = getStepsBySearchString(
      searchFilter,
      stepsWithFilters
    );
    const sortedSteps = getSortedSteps(stepsFromSearch, sortOption);
    const tagsDropdownItems = getTagsDropdownItems(tags);

    return (
      <IndexPage
        className={className}
        title="Flows"
        shouldHideFirebaseSaveIndicator
        subHeaderActions={[
          <CButton
            key="create-flow-button"
            type="positive"
            onClick={this.createFlow}
          >
            Create a Flow
          </CButton>,
          <Dropdown
            attachment={
              <MenuList
                options={[
                  {
                    key: 'manage-tags',
                    value: 'manage-tags',
                    onClick: this.showManagementModal,
                    label: 'Manage Tags',
                  },
                  hasFlowPrioritization && {
                    key: 'flow-priority',
                    value: 'flow-priority',
                    label: <Link to="/priorities">Flow priority</Link>,
                  },
                ].filter(Boolean)}
              />
            }
          >
            {/* TODO: this is semantically wrong since it will render a button inside a semantic button */}
            {({ visible }) => (
              <EllipsisButton
                icon="ellipsis-h"
                kind="secondary"
                active={visible}
              />
            )}
          </Dropdown>,
        ]}
      >
        {hasNoFlows && (
          <CPage.Container>
            <ProductLedExampleFlows
              areProductLedFlowsMinimized={
                usageProperties.hasProductLedFlowContainerBeenDismissed
              }
              hasFlows={false}
            />
            <EmptyState
              header="A flow is an experience you make with Appcues."
              imageSrc={FlowListImg}
            >
              <Text>
                Flows are flexible user experiences. Onboard users, announce new
                features, or dream up something else.
              </Text>
              <CButton type="positive" onClick={this.createFlow}>
                Build Your First Flow
              </CButton>
            </EmptyState>
          </CPage.Container>
        )}
        {!hasNoFlows && (
          <StepViewControls
            users={users}
            tagsDropdownItems={tagsDropdownItems}
            tags={tags}
            flowLayout={flowLayout}
            statusFilter={statusFilter}
            segmentFilter={segmentFilter}
            createdByFilter={createdByFilter}
            searchFilter={searchFilter}
            sortOption={sortOption}
            tagsFilter={tagsFilter}
            stepsForListView={flows}
            stepsFromSearch={stepsFromSearch}
          />
        )}
        {!hasNoFlows && (
          <StepView
            flowLayout={flowLayout}
            meta={meta}
            conditions={conditions}
            users={users}
            tagsDropdownItems={tagsDropdownItems}
            goals={goals}
            segments={segments}
            analytics={flowListAnalytics}
            stepsFromSearch={stepsFromSearch}
            sortedSteps={sortedSteps}
            areProductLedFlowsMinimized={
              usageProperties.hasProductLedFlowContainerBeenDismissed
            }
          />
        )}
      </IndexPage>
    );
  }
}

function mapStateToProps(state) {
  return {
    flows: selectFlowsAsList(state),
    userId: selectUserId(state),
    conditions: selectAccountConditions(state),
    users: selectAccountUsers(state),
    tags: selectAccountTags(state),
    meta: selectAccountMeta(state),
    goals: selectAccountGoals(state),
    segments: selectAccountSegments(state),
    stats: selectAccountStats(state),
    flowListAnalytics: selectFlowListAnalytics(state),
    flowLayout: selectFlowLayout(state),
    hasFlowPrioritization: selectAccountFeature(state, FLOW_PRIORITIZATION),
    statusFilter: selectFlowStatusFilter(state),
    segmentFilter: selectFlowSegmentFilter(state),
    createdByFilter: selectFlowCreatedByFilter(state),
    searchFilter: selectFlowSearchFilter(state),
    tagsFilter: selectFlowTagsFilter(state),
    sortOption: selectFlowSortOption(state),
    hasChromeExtensionInstalled: selectHasChromeExtensionInstalled(state),
    chromeExtensionId: selectChromeExtensionId(state),
    accountId: selectAccountId(state),
    accountLoaded: selectAccountMetaSynced(state),
    usageProperties: selectUserUsageProperties(state),
    loaded:
      selectAreFlowsSynced(state) &&
      selectAccountMetaSynced(state) &&
      selectAccountSegmentsSynced(state) &&
      selectAccountGoalsSynced(state) &&
      selectAccountUsersSynced(state) &&
      selectAccountTagsSynced(state),
  };
}

const mapDispatchToProps = {
  onLoad: fetchRules,
  showModal,
  navigate,
  startBuilderInChromeExtension,
  fetchFlowListAnalytics,
};

export default connect(mapStateToProps, mapDispatchToProps)(StepIndex);
